import { Fragment, useEffect, useState } from "react";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import { PlaceOutlined, Speed } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import axios from "../../api/axios";
import { EQUIPMENTS_ENDPOINT } from "../../consts";
import { useTenantName } from "../../contexts/auth";
import { LatestEquipmentCounter } from "../../types/equipments";
import { formatAddress, formatCounterValue, formatDateTime } from "../../utils";
import { DateTimeLines } from "./relevantDateCell/relevantDateCell.component";

type SelectedEquipment = {
  id: string | number;
  installation_address?: Record<string, unknown> | null;
};

/** The machine's newest reading of each kind (BP, CP for color machines, SP
 * when it has any). `excludeJobId` leaves out readings already recorded on
 * that job, so editing a job shows what its counters followed on from. */
const useLatestCounters = (
  equipmentId: string | number | undefined,
  excludeJobId?: string | number
): LatestEquipmentCounter[] | null => {
  const [counters, setCounters] = useState<LatestEquipmentCounter[] | null>(null);

  useEffect(() => {
    setCounters(null);
    if (equipmentId === undefined) {
      return;
    }

    let isCancelled = false;
    axios
      .get<LatestEquipmentCounter[]>(`${EQUIPMENTS_ENDPOINT}${equipmentId}/latest-counters/`, {
        params: excludeJobId !== undefined ? { exclude_job: excludeJobId } : undefined,
      })
      .then(({ data }) => {
        if (!isCancelled) setCounters(Array.isArray(data) ? data : []);
      })
      // Informational only - the form works the same without it.
      .catch(() => {
        if (!isCancelled) setCounters([]);
      });

    return () => {
      isCancelled = true;
    };
  }, [equipmentId, excludeJobId]);

  return counters;
};

/** Shown under a job task's equipment picker once a machine is chosen: where
 * it is installed (the picker only shows this while its list is open) and
 * its latest counter readings. */
export const SelectedEquipmentDetails = ({
  equipment,
  excludeJobId,
}: {
  equipment: SelectedEquipment | null;
  excludeJobId?: string | number;
}) => {
  const { t } = useTranslation();
  // Equipment with no installation address sits with us, not at a client.
  const tenantName = useTenantName();
  const counters = useLatestCounters(equipment?.id, excludeJobId);

  if (!equipment) {
    return null;
  }

  const address = formatAddress(equipment.installation_address || null);

  return (
    <Box sx={{ mt: 0.75, display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
        <Tooltip title={t("Installation Address")}>
          <PlaceOutlined sx={{ fontSize: 16, color: "text.secondary", flexShrink: 0 }} />
        </Tooltip>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontStyle: address ? "normal" : "italic", overflowWrap: "anywhere" }}
        >
          {address || tenantName}
        </Typography>
      </Box>
      {counters !== null && (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, minWidth: 0 }}>
          <Tooltip title={t("Latest counter readings")}>
            <Speed sx={{ fontSize: 16, color: "text.secondary", flexShrink: 0, mt: 0.25 }} />
          </Tooltip>
          {counters.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
              {t("No counter reading yet")}
            </Typography>
          ) : (
            // One reading per line - kind and value, then when it was read in
            // the same date-over-time styling as the jobs list's Relevant date.
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "max-content max-content",
                alignItems: "center",
                columnGap: 1,
                rowGap: 0.5,
              }}
            >
              {counters.map((counter) => {
                const [date, time] = formatDateTime(counter.read_at).split("\n");
                return (
                  <Fragment key={counter.id}>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={
                        <>
                          {`${counter.kind_display}: `}
                          <Box component="span" sx={{ fontWeight: 600 }}>
                            {formatCounterValue(counter.value)}
                          </Box>
                        </>
                      }
                      sx={{ justifySelf: "start" }}
                    />
                    <Box>
                      <DateTimeLines date={date} time={time} />
                    </Box>
                  </Fragment>
                );
              })}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
